import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { PgAddress } from '@trashlab/api/src/domains/core/types';
import { sql } from 'drizzle-orm';
import {
  boolean,
  foreignKey,
  index,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { organization } from '@trashlab/api/src/domains/identity/schemas/organization';

// Define the customer contact type enum
export const customer_contact_type = pgEnum('customer_contact_type', [
  'BILLING',
  'SITE',
  'LEAD',
  'STAFF',
]);

// Add conversation status enum for OpenPhone-style open/done model
export const conversation_status = pgEnum('conversation_status', [
  'OPEN',
  'DONE',
]);

export const customer_contact = pgTable(
  'customer_contact',
  {
    ...defaultFields.fieldsWithTenantId(),
    name: varchar('name', { length: 255 }).notNull(),
    phoneNumber: varchar('phoneNumber', { length: 255 }),
    email: varchar('email', { length: 255 }),
    address: jsonb('address')
      .notNull()
      .$type<PgAddress>()
      .default(sql`'{}'::jsonb`),
    addressString: text('addressString'),
    latitude: varchar('latitude', { length: 20 }).notNull(),
    longitude: varchar('longitude', { length: 20 }).notNull(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    searchables: text('searchables'),
    types: customer_contact_type('types')
      .array()
      .default(sql`'{BILLING,SITE}'::customer_contact_type[]`)
      .notNull(),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    parentId: uuid('parentId'),
    supabaseUserId: uuid('supabaseUserId'),
    notes: text('notes'),
    commAssignedUserId: uuid('commAssignedUserId'),
    // Used in automated outbound comms
    preferredName: varchar('preferredName', { length: 255 }).default(sql`''`),
    title: varchar('title', { length: 100 }),
    preferEmail: boolean('preferEmail').default(true),
    preferSMS: boolean('preferSMS').default(true),
    // OpenPhone-style conversation status
    conversationStatus: conversation_status('conversationStatus')
      .default('DONE')
      .notNull(),
    // Block list: whether this contact is blocked from comms center
    isBlocked: boolean('isBlocked').default(false).notNull(),
    // Whether this contact is a fallback contact for call resilience
    isFallback: boolean('isFallback'),
    // Denormalized timestamp fields for performance optimization of contact list queries
    // These eliminate expensive scalar subqueries in buildFetchContactsListQuery
    // lastUserCommCreatedAt: Latest non-automated communication timestamp
    lastUserCommCreatedAt: timestamp('lastUserCommCreatedAt', {
      withTimezone: true,
      mode: 'string',
    }),
    // lastAutomatedCommCreatedAt: Latest automated communication timestamp
    lastAutomatedCommCreatedAt: timestamp('lastAutomatedCommCreatedAt', {
      withTimezone: true,
      mode: 'string',
    }),
    // lastUserCommentCreatedAt: Latest non-AI comment timestamp
    lastUserCommentCreatedAt: timestamp('lastUserCommentCreatedAt', {
      withTimezone: true,
      mode: 'string',
    }),
    // lastAutomatedCommentCreatedAt: Latest AI-generated comment timestamp
    lastAutomatedCommentCreatedAt: timestamp('lastAutomatedCommentCreatedAt', {
      withTimezone: true,
      mode: 'string',
    }),
  },
  (table) => {
    return {
      tenantIdUnique: uniqueIndex('customer_contact_tenant_id_key').on(
        table.tenantId,
        table.id,
      ),
      comm_assigned_user_id_idx: index(
        'customer_contact_comm_assigned_user_id_idx',
      ).on(table.commAssignedUserId),
      createdAtTs_idx: index('customer_contact_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      supabase_user_id_idx: index('customer_contact_supabase_user_id_idx').on(
        table.supabaseUserId,
      ),
      tenantId_idx: index('customer_contact_tenantId_idx').on(table.tenantId),
      tenantId_createdAtTs_idx: index(
        'customer_contact_tenantId_createdAtTs_idx',
      ).on(table.tenantId, table.createdAtTs),
      trigram: index('customer_contact_trigram').using(
        'gin',
        table.tenantId,
        table.searchables.op('gin_trgm_ops'),
      ),
      consoleCommsNameTrigram: index(
        'customer_contact_console_comms_name_trgm_idx',
      ).using('gin', table.name.op('gin_trgm_ops')),
      // Partial trigram index for non-archived contacts - more efficient for search queries
      trigram_active: index('customer_contact_trigram_active')
        .using('gin', table.tenantId, table.searchables.op('gin_trgm_ops'))
        .where(sql`${table.archivedAtTs} IS NULL`),
      updatedAtTs_idx: index('customer_contact_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('customer_contact_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('customer_contact_versionNumber_idx').on(
        table.versionNumber,
      ),
      customer_contact_phone_number_idx: index(
        'customer_contact_phone_number_idx',
      ).on(table.phoneNumber),
      tenant_phone_active_idx: index('customer_contact_tenant_phone_active_idx')
        .on(table.tenantId, table.phoneNumber)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.phoneNumber} IS NOT NULL`,
        ),
      broadcast_blocked_email_idx: index(
        'customer_contact_broadcast_blocked_email_idx',
      )
        .on(table.tenantId, sql`lower(btrim(${table.email}))`)
        .where(sql`${table.isBlocked} = true`),
      tenant_lower_email_active_idx: index(
        'customer_contact_tenant_lower_email_active_idx',
      )
        .on(table.tenantId, sql`lower(${table.email})`)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.email} IS NOT NULL`,
        ),
      // Add conversation status index for OpenPhone-style filtering
      open_conversations_idx: index('customer_contact_open_idx')
        .on(table.tenantId, table.conversationStatus, table.id)
        .where(sql`${table.conversationStatus} = 'OPEN'`),
      // Index for filtering by conversationStatus (used in fetchContactsList)
      // Includes id to enable index-only scans and improve selectivity
      tenant_conversation_status_idx: index(
        'idx_customer_contact_tenant_conversation_status',
      ).on(table.tenantId, table.conversationStatus, table.id),
      // Optimized index for omniSearch contacts query: filters by tenantId, orders by updatedAtTs
      // Partial index only includes non-archived contacts (archivedAtTs IS NULL)
      search_optimized_idx: index('idx_customer_contact_search_optimized')
        .on(table.tenantId, table.updatedAtTs.desc())
        .where(sql`${table.archivedAtTs} IS NULL`),
      // Perfect index for omniSearch: GIN index on tenantId + searchables (trigram) for fast text search
      // Partial index for non-archived contacts. PostgreSQL will use this for text search filtering,
      // then can efficiently sort the results by updatedAtTs using the search_optimized_idx
      omnisearch_perfect_idx: index('idx_customer_contact_omnisearch_perfect')
        .using('gin', table.tenantId, table.searchables.op('gin_trgm_ops'))
        .where(sql`${table.archivedAtTs} IS NULL`),
      // Index for contact list sorting by latest interaction timestamp
      // Supports GREATEST(lastUserCommCreatedAt, lastUserCommentCreatedAt) ordering
      // Partial index only includes non-archived contacts
      latest_interaction_idx: index('idx_customer_contact_latest_interaction')
        .on(
          table.tenantId,
          table.lastUserCommCreatedAt.desc(),
          table.lastUserCommentCreatedAt.desc(),
        )
        .where(sql`${table.archivedAtTs} IS NULL`),

      // Partial index for blocked contacts (created CONCURRENTLY on prod)
      blocked_contacts_idx: index('customer_contact_blocked_idx')
        .on(table.tenantId, table.id)
        .where(sql`${table.isBlocked} = true`),

      //foreign keys
      tenant_id_fkey: foreignKey({
        columns: [table.tenantId],
        foreignColumns: [organization.id],
        name: 'customer_contact_tenant_id_fkey',
      }),
    };
  },
);

export const customer_contact_history = pgTable('customer_contact_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  name: varchar('name', { length: 255 }),
  phoneNumber: varchar('phoneNumber', { length: 255 }),
  email: varchar('email', { length: 255 }),
  address: jsonb('address').$type<PgAddress>(),
  addressString: text('addressString'),
  latitude: varchar('latitude', { length: 20 }),
  longitude: varchar('longitude', { length: 20 }),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  searchables: text('searchables'),
  customer_contact_type: customer_contact_type('types').array(),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  parentId: uuid('parentId'),
  supabaseUserId: uuid('supabaseUserId'),
  notes: text('notes'),
  commAssignedUserId: uuid('commAssignedUserId'),
  preferredName: varchar('preferredName', { length: 255 }),
  title: varchar('title', { length: 100 }),
  preferEmail: boolean('preferEmail'),
  preferSMS: boolean('preferSMS'),
  conversationStatus: conversation_status('conversationStatus'),
  isBlocked: boolean('isBlocked'),
  isFallback: boolean('isFallback'),
  // Denormalized timestamp fields for performance optimization
  lastUserCommCreatedAt: timestamp('lastUserCommCreatedAt', {
    withTimezone: true,
    mode: 'string',
  }),
  lastAutomatedCommCreatedAt: timestamp('lastAutomatedCommCreatedAt', {
    withTimezone: true,
    mode: 'string',
  }),
  lastUserCommentCreatedAt: timestamp('lastUserCommentCreatedAt', {
    withTimezone: true,
    mode: 'string',
  }),
  lastAutomatedCommentCreatedAt: timestamp('lastAutomatedCommentCreatedAt', {
    withTimezone: true,
    mode: 'string',
  }),
});

export const customer_contact_relation = pgTable(
  'customer_contact_relation',
  {
    ...defaultFields.fieldsWithTenantId(),
    customerContactId: uuid('customerContactId').notNull(),
    customerSiteId: uuid('customerSiteId'),
    customerId: uuid('customerId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
  },
  (table) => {
    return {
      createdAtTs_idx: index('customer_contact_relation_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('customer_contact_relation_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('customer_contact_relation_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('customer_contact_relation_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index(
        'customer_contact_relation_versionNumber_idx',
      ).on(table.versionNumber),
      customerContactId_idx: index(
        'customer_contact_relation_customerContactId_idx',
      ).on(table.customerContactId),
      tenant_site_id_idx: index('customer_contact_relation_tenant_site_id_idx')
        .on(table.tenantId, table.customerSiteId, table.id)
        .where(sql`${table.customerSiteId} IS NOT NULL`),
      tenant_customer_id_idx: index(
        'customer_contact_relation_tenant_customer_id_idx',
      ).on(table.tenantId, table.customerId, table.id),
    };
  },
);

export const customer_contact_relation_history = pgTable(
  'customer_contact_relation_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    customerContactId: uuid('customerContactId'),
    customerSiteId: uuid('customerSiteId'),
    customerId: uuid('customerId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
  },
);
